/**
 * 👍 / 👎 footer + "Save to model" CTA for assistant messages.
 *
 * R-143-CHAT-ASSISTANT Phase D.
 *
 * Hidden when the message has already been saved as a curated insight
 * (replaced by a "✓ Saved to model" pill instead).
 */
import React from 'react';
import { ThumbsUp, ThumbsDown, Check, Share2, Loader2 } from 'lucide-react';

import type { ChatMessage } from '../../types/chat';

interface Props {
  message: ChatMessage;
  /** Triggered when 👍 is pressed; parent opens SaveToModelDialog. */
  onAccept: () => void;
  /** Triggered when 👎 is pressed; calls denyInsight CF. */
  onDeny: () => void;
  /** Triggered when "Share with team" pill is clicked post-save. */
  onShare?: () => void;
  busy?: boolean;
}

export function MessageFeedback({
  message,
  onAccept,
  onDeny,
  onShare,
  busy,
}: Props): React.JSX.Element {
  if (message.curatedInsightId) {
    return (
      <div
        className="flex items-center gap-2 mt-2 text-[9px] uppercase tracking-widest text-success"
        data-testid="message-feedback-saved"
      >
        <Check className="w-3 h-3" aria-hidden="true" />
        <span>Saved to model</span>
        {onShare && (
          <button
            type="button"
            onClick={onShare}
            className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-[8px] uppercase tracking-widest border border-border-subtle text-text-muted hover:text-primary hover:border-primary"
            data-testid="message-feedback-share"
          >
            <Share2 className="w-3 h-3" aria-hidden="true" />
            Share with team
          </button>
        )}
      </div>
    );
  }

  if (message.feedback === 'negative') {
    return (
      <div
        className="text-[9px] uppercase tracking-widest text-text-muted mt-2"
        data-testid="message-feedback-denied"
      >
        Marked as not helpful · feedback recorded
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 mt-2"
      data-testid="message-feedback-prompt"
    >
      <button
        type="button"
        onClick={onAccept}
        disabled={busy}
        className="inline-flex items-center gap-1 px-2 py-1 text-[9px] uppercase tracking-widest border border-border-subtle text-text-muted hover:text-success hover:border-success disabled:opacity-50"
        data-testid="message-feedback-accept"
        aria-label="Save this answer to the Datacrate model"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ThumbsUp className="w-3 h-3" aria-hidden="true" />}
        Helpful
      </button>
      <button
        type="button"
        onClick={onDeny}
        disabled={busy}
        className="inline-flex items-center gap-1 px-2 py-1 text-[9px] uppercase tracking-widest border border-border-subtle text-text-muted hover:text-danger hover:border-danger disabled:opacity-50"
        data-testid="message-feedback-deny"
        aria-label="Mark this answer as not helpful"
      >
        <ThumbsDown className="w-3 h-3" aria-hidden="true" />
        Not helpful
      </button>
    </div>
  );
}
